import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bot, Loader2, Sparkles, X } from "lucide-react";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@tubo/ui/components/ui/resizable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import { BotRunOutput } from "@client/components/bots/run/bot-run-output";
import { MarkdownRenderer } from "@tubo/ui/components/markdown";
import {
  getListBotRunArtifactsQueryKey,
  getListBotRunsQueryKey,
  useCancelBotRun,
  useListBotRuns,
  useStartBotAgentRun,
} from "@tubo/api-models/hooks/bot-run-api.hooks";
import {
  getListBotsQueryKey,
  useListBots,
} from "@tubo/api-models/hooks/chat-bot-api.hooks";
import { useGenerateBotSuggestions } from "@tubo/api-models/hooks/bot-builder-api.hooks";
import type { BotInputVariableVO } from "@tubo/api-models/hooks/common.schemas";
import { toast } from "sonner";
import { useBotRunStream } from "@client/components/bots/run/use-bot-run-stream";
import { RunEventTimeline } from "@client/components/bots/run/run-event-timeline";
import { ArtifactPanel } from "@client/components/bots/run/artifact-panel";
import { RunComposer } from "@client/components/bots/run/run-composer";

export const Route = createFileRoute("/_app/bots/$botId/run")({
  component: BotRunPage,
});

const RUN_STATUS_LABELS: Record<string, string> = {
  running: "执行中",
  succeeded: "已完成",
  failed: "失败",
  cancelled: "已取消",
};

function StatusBadge({ status }: { status: string }) {
  if (status === "running") {
    return (
      <Badge variant="default" className="gap-1">
        <Loader2 className="size-3 animate-spin" />
        执行中
      </Badge>
    );
  }
  if (status === "succeeded") return <Badge variant="secondary" className="text-emerald-600">已完成</Badge>;
  if (status === "failed") return <Badge variant="destructive">失败</Badge>;
  if (status === "cancelled") return <Badge variant="outline">已取消</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

/** 运行成功后的追问建议 chips（suggestionMode=auto 时由 LLM 生成） */
function RunSuggestions({
  suggestions,
  isPending,
  onPick,
}: {
  suggestions: string[];
  isPending: boolean;
  onPick: (text: string) => void;
}) {
  if (!isPending && suggestions.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
      {isPending && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" />
          正在生成追问建议…
        </span>
      )}
      {suggestions.map((suggestion) => (
        <button
          key={suggestion}
          type="button"
          className="flex items-center gap-1.5 rounded-full border bg-background px-3 py-1.5 text-xs transition-colors hover:bg-accent"
          onClick={() => onPick(suggestion)}
        >
          <Sparkles className="size-3 text-primary" />
          {suggestion}
        </button>
      ))}
    </div>
  );
}

function BotRunPage() {
  const { botId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: botsResponse, isLoading: isLoadingBots } = useListBots();
  const { data: runsResponse } = useListBotRuns(botId, {
    query: { refetchInterval: 8_000 },
  });
  const [input, setInput] = useState("");
  const [supplement, setSupplement] = useState("");
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<Array<{ fileId: string; name: string }>>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const bot = botsResponse?.bots?.find((item) => item.id === botId);
  const runs = useMemo(() => runsResponse?.runs ?? [], [runsResponse]);
  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;
  const { events } = useBotRunStream(botId, selectedRunId);
  const inputVariables = useMemo<BotInputVariableVO[]>(
    () => bot?.publishedInputVariables ?? [],
    [bot?.publishedInputVariables],
  );
  const composerMode = bot?.agentUiMode === "hybrid"
    ? "hybrid"
    : bot?.agentUiMode === "form" ? "form" : "chat";

  // 默认选中：优先运行中的任务，否则最近一次
  useEffect(() => {
    if (selectedRunId || runs.length === 0) return;
    setSelectedRunId(runs.find((run) => run.status === "running")?.id ?? runs[0].id);
  }, [runs, selectedRunId]);

  // 事件驱动刷新：产物事件刷产物列表，终态事件刷运行记录与产物
  useEffect(() => {
    const last = events[events.length - 1];
    if (!last) return;
    if (last.type === "artifact") {
      void queryClient.invalidateQueries({ queryKey: getListBotRunArtifactsQueryKey(botId, selectedRunId ?? "") });
    }
    if (last.step === "run.completed" || last.step === "run.failed" || last.step === "run.cancelled") {
      void queryClient.invalidateQueries({ queryKey: getListBotRunsQueryKey(botId) });
      void queryClient.invalidateQueries({ queryKey: getListBotRunArtifactsQueryKey(botId, selectedRunId ?? "") });
    }
  }, [events, botId, selectedRunId, queryClient]);

  const cancelMutation = useCancelBotRun();
  const [autoSuggestions, setAutoSuggestions] = useState<string[]>([]);
  const suggestionsMutation = useGenerateBotSuggestions();

  // suggestionMode=auto：运行成功后基于本次输入/输出生成追问建议
  const succeededRunId = selectedRun?.status === "succeeded" && selectedRun.output ? selectedRun.id : null;
  useEffect(() => {
    setAutoSuggestions([]);
    if (!succeededRunId || bot?.suggestionMode !== "auto" || !selectedRun) return;
    suggestionsMutation.mutate(
      { botId, data: { input: selectedRun.input ?? "", output: selectedRun.output ?? "" } },
      { onSuccess: (resp) => setAutoSuggestions(resp.suggestions ?? []) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [succeededRunId, bot?.suggestionMode, botId]);

  const startMutation = useStartBotAgentRun({
    mutation: {
      onSuccess: (resp) => {
        queryClient.invalidateQueries({ queryKey: getListBotRunsQueryKey(botId) });
        queryClient.invalidateQueries({ queryKey: getListBotsQueryKey() });
        setInput("");
        setSupplement("");
        setVariableValues({});
        setAttachments([]);
        if (resp?.runId) setSelectedRunId(resp.runId);
        toast.success("已提交执行任务");
      },
      onError: () => toast.error("任务启动失败，请确认 Bot 已发布且你有使用权限"),
    },
  });

  const startRun = () => {
    const request = input.trim();
    if (!request) return;
    startMutation.mutate({ botId, data: { input: request } });
  };

  /** 表单模式提交：必填校验后组装任务输入与变量值 */
  const startFormRun = () => {
    for (const v of inputVariables) {
      if (v.required && !variableValues[v.name]?.trim()) {
        toast.error(`请填写「${v.label || v.name}」`);
        return;
      }
    }
    const params = inputVariables
      .map((v) => `${v.label || v.name}: ${variableValues[v.name] ?? v.defaultValue ?? ""}`)
      .filter((line) => !line.endsWith(": "))
      .join("；");
    const extra = (input.trim() || supplement.trim());
    const taskInput = extra || (params ? `请按任务参数执行：${params}` : "");
    if (!taskInput) {
      toast.error("请填写任务参数或补充说明");
      return;
    }
    startMutation.mutate({
      botId,
      data: {
        input: taskInput,
        variables: variableValues,
        attachmentFileIds: attachments.length > 0 ? attachments.map((a) => a.fileId) : undefined,
      },
    });
  };

  if (isLoadingBots) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">正在加载智能体…</div>;
  }

  if (!bot || !bot.publishedTaskExecutionEnabled) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <Bot className="size-8 text-muted-foreground" />
        <p className="font-medium">该智能体尚未发布可用任务版本或你无权使用</p>
        <Button variant="outline" onClick={() => navigate({ to: "/bot-market" })}>返回智能体市场</Button>
      </div>
    );
  }

  const runItems = Object.fromEntries(
    runs.map((run) => [
      run.id,
      `${RUN_STATUS_LABELS[run.status] ?? run.status} · ${(run.input ?? "").slice(0, 16)}${
        run.completedAt ? ` · ${new Date(run.completedAt).toLocaleString()}` : ""
      }`,
    ]),
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
        <Button variant="ghost" size="icon" aria-label="返回智能体市场" onClick={() => navigate({ to: "/bot-market" })}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{bot.name}</p>
          <p className="truncate text-xs text-muted-foreground">{bot.desc || "提交任务后，Agent 会在后台完成执行。"}</p>
        </div>
        {selectedRun && <StatusBadge status={selectedRun.status} />}
        {selectedRun && runs.length > 0 && (
          <Select
            value={selectedRunId ?? undefined}
            onValueChange={(value) => setSelectedRunId(value ?? null)}
            items={runItems}
          >
            <SelectTrigger className="w-64" aria-label="选择运行记录">
              <SelectValue placeholder="选择运行记录" />
            </SelectTrigger>
            <SelectContent>
              {runs.map((run) => (
                <SelectItem key={run.id} value={run.id}>
                  {runItems[run.id]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {selectedRun?.status === "running" && (
          <Button
            variant="outline"
            size="sm"
            disabled={cancelMutation.isPending}
            onClick={() => {
              if (!selectedRun) return;
              cancelMutation.mutate({ botId, runId: selectedRun.id }, {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: getListBotRunsQueryKey(botId) });
                  toast.success("已发送取消请求");
                },
                onError: () => toast.error("取消失败"),
              });
            }}
          >
            <X className="size-3" />
            取消运行
          </Button>
        )}
      </header>

      <ResizablePanelGroup className="min-h-0 flex-1">
        <ResizablePanel defaultSize="60%" minSize="40%">
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <div className="mx-auto max-w-2xl">
                {runs.length === 0 && (
                  <div className="mb-6 rounded-lg border bg-card p-5">
                    {bot.openingStatement ? (
                      <div className="text-sm leading-6">
                        <MarkdownRenderer content={bot.openingStatement} />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">提交任务后，Agent 会在后台完成执行。</p>
                    )}
                    {(bot.openingSuggestions?.length ?? 0) > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {bot.openingSuggestions?.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            className="flex items-center gap-1.5 rounded-full border bg-background px-3 py-1.5 text-xs transition-colors hover:bg-accent"
                            onClick={() => (composerMode === "chat" ? setInput(suggestion) : setSupplement(suggestion))}
                          >
                            <Sparkles className="size-3 text-primary" />
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {selectedRun?.status === "succeeded" && selectedRun.output ? (
                  <div className="mb-6 rounded-lg border bg-card p-4">
                    <h2 className="mb-2 text-sm font-semibold">执行结果</h2>
                    <div className="text-sm">
                      <BotRunOutput key={selectedRun.id} output={selectedRun.output} citations={selectedRun.citations} />
                    </div>
                    <RunSuggestions
                      suggestions={autoSuggestions}
                      isPending={suggestionsMutation.isPending}
                      onPick={(text) => (composerMode === "chat" ? setInput(text) : setSupplement(text))}
                    />
                  </div>
                ) : null}
                {selectedRun?.error ? (
                  <div className="mb-6 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
                    失败原因：{selectedRun.error}
                  </div>
                ) : null}
                <RunEventTimeline events={events} />
              </div>
            </div>
            <div className="shrink-0 border-t p-4">
              <RunComposer
                mode={composerMode}
                inputVariables={inputVariables}
                input={input}
                onInputChange={setInput}
                supplement={supplement}
                onSupplementChange={setSupplement}
                variableValues={variableValues}
                onVariableValuesChange={setVariableValues}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                isPending={startMutation.isPending}
                onSubmit={composerMode === "chat" ? startRun : startFormRun}
              />
            </div>
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize="40%" minSize="25%">
          <ArtifactPanel mode="bot-run" botId={botId} runId={selectedRunId} />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
